import { useCallback, useState } from 'react'
import {
  type CalendarDensity,
  type CalendarNamePriority,
  type CalendarSortBy,
  type WeeksMode,
} from '../MonthCalendarGrid/types'
import { type CalendarItemKind } from '../types'

/** The kinds the month view can be asked to show. */
export const MONTH_VIEW_KINDS: readonly CalendarItemKind[] = [
  'moment',
  'content',
]

/**
 * Everything the View menu decides. Each setting is remembered on its own, per
 * workspace, so a person's calendar looks the same the next time they open it.
 *
 * Settings that belong to one object family say so in their name. Only content
 * has any: a moment bar draws itself the one way, so the menu has nothing to
 * ask about it beyond whether the calendar shows it at all.
 */
export type MonthViewSettings = Readonly<{
  /**
   * The label set the calendar takes its colours from. Absent means the colour
   * a person set on the item itself — its primary label, or its moment's.
   */
  colorLabelSetId?: string
  contentDensity: CalendarDensity
  /** Which of a content item's two names it leads with. Shared with the week view. */
  namePriority: CalendarNamePriority
  sortBy: CalendarSortBy
  /**
   * Draw a piece of content across the days it is in market, instead of as a
   * card on the day it launched.
   */
  shouldShowContentDuration: boolean
  /** Show the colour a content chip inherits from its moment. */
  shouldShowContentLabelColor: boolean
  /**
   * Show the content's own name. Off, content inside a moment is named by the
   * moment alone.
   */
  shouldShowContentName: boolean
  /** Show the moment a piece of content belongs to. Cards only. */
  shouldShowMomentBadge: boolean
  shouldShowTime: boolean
  /** Show where a piece of content has reached in its workflow. */
  shouldShowWorkflow: boolean
  /** Which kinds the calendar draws. Never empty. */
  shownKinds: readonly CalendarItemKind[]
  weeksMode: WeeksMode
}>

const oneOf =
  <T extends string>(values: readonly T[]) =>
  (raw: string): T | undefined =>
    values.find((value) => value === raw)

const boolean = (raw: string): boolean | undefined =>
  raw === 'true' ? true : raw === 'false' ? false : undefined

const kinds = (raw: string): readonly CalendarItemKind[] | undefined => {
  const chosen = MONTH_VIEW_KINDS.filter((kind) =>
    raw.split(',').includes(kind)
  )
  // An empty set would draw an empty month with no way back, so it is treated
  // as nothing stored rather than as a choice.
  return chosen.length === 0 ? undefined : chosen
}

type SettingCodec<K extends keyof MonthViewSettings> = {
  decode: (raw: string) => MonthViewSettings[K] | undefined
  /**
   * How the value is written. Defaults to the plain text every month key
   * holds; a key shared with another view is written the way that view
   * writes it.
   */
  encode?: (value: NonNullable<MonthViewSettings[K]>) => string
  fallback: MonthViewSettings[K]
  /**
   * Suffix of the key the setting is stored under. Changing one forgets what
   * people chose.
   */
  name: string
  /**
   * The whole key of a setting the month keeps with another view rather than
   * to itself. It is not scoped to the workspace, because the view it is
   * shared with does not scope it either.
   */
  sharedKey?: string
  /**
   * The answer to take from another view when this one has none of its own. It
   * is read once and never written back: the two views are seeded from each
   * other, not kept in step.
   */
  seedFrom?: (workspaceUuid: string) => MonthViewSettings[K] | undefined
  /**
   * Suffix of the key one answer was stored under before the menu started
   * asking about moments and content separately. It is read when the setting's
   * own key holds nothing, and never written, so the day the menu split in two
   * takes nobody's choice away.
   */
  splitFrom?: string
}

/**
 * The sort the week view is already using in this workspace, read as a month
 * sort.
 *
 * The week view is a package of its own, and it imports this one, so its key
 * and the words it stores are written out here rather than imported: reaching
 * back into it would make the two packages depend on each other.
 *
 * A sort the month cannot make — the week's time of day — reads as no sort, and
 * every label set the week can sort by reads as the month's primary label.
 */
const WEEK_VIEW_SORTS: Readonly<Record<string, CalendarSortBy>> = {
  account: 'account',
  channel: 'channel',
  'label set': 'label',
  none: 'none',
  time: 'none',
}

// Storage a browser refuses to hand over reads as nothing stored.
const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const readWeekViewSort = (
  workspaceUuid: string
): CalendarSortBy | undefined => {
  const raw = readStored(`week-view-selected-sort:${workspaceUuid}`)
  if (raw === null) return undefined

  try {
    const stored: unknown = JSON.parse(raw)
    const type =
      typeof stored === 'object' && stored !== null && 'type' in stored
        ? stored.type
        : undefined

    return typeof type === 'string' ? WEEK_VIEW_SORTS[type] : undefined
  } catch {
    return undefined
  }
}

/**
 * The text inside a JSON string, for the keys the week view writes with
 * `beautiful-react-hooks`. Anything else — including a key written as plain
 * text by hand — reads as nothing.
 */
const jsonString = (raw: string): string | undefined => {
  try {
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'string' ? parsed : undefined
  } catch {
    return undefined
  }
}

const codecs: { [K in keyof MonthViewSettings]-?: SettingCodec<K> } = {
  colorLabelSetId: {
    // An empty key is nobody's label set, and reading it back as one would
    // colour the month by nothing at all.
    decode: (raw) => (raw === '' ? undefined : raw),
    fallback: undefined,
    name: 'color-label-set',
    // Week view stores v1 label-set ids; seed when it stores uuids.
  },
  contentDensity: {
    // The menu offered a taller card — `cozy` — for a day. Anyone who chose it
    // gets the card it was a bigger version of, not a row.
    decode: (raw) =>
      raw === 'cozy' ? 'card' : oneOf<CalendarDensity>(['row', 'card'])(raw),
    fallback: 'row',
    name: 'density-content',
    splitFrom: 'density',
  },
  namePriority: {
    // The week view writes this key as JSON, so the month reads and writes it
    // the same way; a plain word is read too, so a hand-edited key still works.
    decode: (raw) =>
      oneOf<CalendarNamePriority>(['content', 'moment'])(
        jsonString(raw) ?? raw
      ),
    encode: (value) => JSON.stringify(value),
    fallback: 'content',
    name: 'content-name-priority',
    sharedKey: 'week-view-content-name-priority',
  },
  sortBy: {
    decode: oneOf<CalendarSortBy>([
      'none',
      'account',
      'channel',
      'label',
      'moment',
    ]),
    fallback: 'none',
    name: 'sort-by',
    seedFrom: readWeekViewSort,
  },
  shouldShowContentDuration: {
    decode: boolean,
    fallback: true,
    name: 'in-market',
  },
  shouldShowContentLabelColor: {
    decode: boolean,
    fallback: true,
    name: 'card-label-color-content',
    splitFrom: 'card-label-color',
  },
  shouldShowContentName: {
    decode: boolean,
    fallback: true,
    name: 'card-content-name',
  },
  shouldShowMomentBadge: {
    decode: boolean,
    fallback: true,
    name: 'card-moment',
  },
  shouldShowTime: { decode: boolean, fallback: true, name: 'card-time' },
  shouldShowWorkflow: {
    decode: boolean,
    fallback: true,
    name: 'card-workflow',
  },
  shownKinds: { decode: kinds, fallback: MONTH_VIEW_KINDS, name: 'show' },
  weeksMode: {
    decode: oneOf<WeeksMode>(['fixed', 'scroll']),
    fallback: 'fixed',
    name: 'weeks-mode',
  },
}

const storageKey = (name: string, workspaceUuid: string) =>
  `month-view-${name}:${workspaceUuid}`

const keyOf = (
  codec: { name: string; sharedKey?: string },
  workspaceUuid: string
) => codec.sharedKey ?? storageKey(codec.name, workspaceUuid)

// Values are strings, booleans or a list of kinds, and each reads back the way
// it was written, so one encoder answers for all of them.
const encode = (value: MonthViewSettings[keyof MonthViewSettings]): string =>
  Array.isArray(value) ? value.join(',') : String(value)

const readSettings = (workspaceUuid: string): MonthViewSettings => {
  const read = <K extends keyof MonthViewSettings>(
    setting: K
  ): MonthViewSettings[K] => {
    const codec = codecs[setting]
    const stored = (name: string) => readStored(storageKey(name, workspaceUuid))
    const raw =
      readStored(keyOf(codec, workspaceUuid)) ??
      (codec.splitFrom === undefined ? null : stored(codec.splitFrom))

    // One setting has no answer of its own — the label set to colour by — so
    // the chain can end in `undefined`, which TypeScript cannot tell apart
    // from a missing answer once the settings are read through one generic.
    return ((raw === null ? undefined : codec.decode(raw)) ??
      codec.seedFrom?.(workspaceUuid) ??
      codec.fallback) as MonthViewSettings[K]
  }

  return {
    colorLabelSetId: read('colorLabelSetId'),
    contentDensity: read('contentDensity'),
    namePriority: read('namePriority'),
    sortBy: read('sortBy'),
    shouldShowContentDuration: read('shouldShowContentDuration'),
    shouldShowContentLabelColor: read('shouldShowContentLabelColor'),
    shouldShowContentName: read('shouldShowContentName'),
    shouldShowMomentBadge: read('shouldShowMomentBadge'),
    shouldShowTime: read('shouldShowTime'),
    shouldShowWorkflow: read('shouldShowWorkflow'),
    shownKinds: read('shownKinds'),
    weeksMode: read('weeksMode'),
  }
}

export type UseMonthViewSettingsResult = Readonly<{
  settings: MonthViewSettings
  update: (change: Partial<MonthViewSettings>) => void
}>

/**
 * The View menu's state, remembered per workspace.
 *
 * Storage is scoped to the workspace because the ids inside these settings —
 * and what a person wants to see — do not carry from one workspace to the next.
 */
export const useMonthViewSettings = (
  workspaceUuid: string
): UseMonthViewSettingsResult => {
  const [settings, setSettings] = useState(() => readSettings(workspaceUuid))

  const update = useCallback(
    (change: Partial<MonthViewSettings>) => {
      try {
        Object.entries(change).forEach(([setting, value]) => {
          const codec = codecs[setting as keyof MonthViewSettings]
          const key = keyOf(codec, workspaceUuid)

          if (value !== undefined) {
            window.localStorage.setItem(
              key,
              (codec.encode as ((value: unknown) => string) | undefined)?.(
                value
              ) ?? encode(value)
            )
            return
          }

          // Only a setting that can have no answer — no label set to colour by —
          // is set back to having none by taking its key away. Where a setting
          // always has an answer, `undefined` is a caller with nothing to say
          // rather than a choice, and the stored answer stands.
          if (codec.fallback === undefined) window.localStorage.removeItem(key)
        })
      } catch {
        // A browser that will not remember is not a reason to stop changing the view.
      }
      setSettings((current) => ({ ...current, ...change }))
    },
    [workspaceUuid]
  )

  return { settings, update }
}
